import { Component, lazy, type ComponentType, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@mt/ui';
import { FullScreenMessage } from '../components/common.tsx';
import { reportError } from '../lib/telemetry.ts';

const RELOAD_KEY = 'mt:stale-chunk-reload-at';
/** Don't reload again within this window, so a genuinely broken deploy can't loop. */
export const STALE_RELOAD_WINDOW_MS = 30_000;

/** True when a failed page-code load should trigger a one-time reload. */
export function shouldReloadForStaleChunk(now: number, lastReloadAt: number | null): boolean {
  if (lastReloadAt === null || Number.isNaN(lastReloadAt)) return true;
  const elapsed = now - lastReloadAt;
  return elapsed < 0 || elapsed >= STALE_RELOAD_WINDOW_MS;
}

function tryStaleChunkReload(beforeReload?: () => void): boolean {
  let last: number | null = null;
  try {
    const raw = window.sessionStorage.getItem(RELOAD_KEY);
    last = raw === null ? null : Number(raw);
  } catch {
    /* storage unavailable: fall through to a single reload attempt */
  }
  if (!shouldReloadForStaleChunk(Date.now(), last)) return false;
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  beforeReload?.();
  window.location.reload();
  return true;
}

/**
 * Set as soon as the page starts being replaced. Navigating away cancels any
 * page code still downloading, which rejects its import — that is not a stale
 * deploy, and reloading then would hijack the navigation the user just started.
 */
let leaving = false;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    leaving = true;
  });
  window.addEventListener('pagehide', () => {
    leaving = true;
  });
  window.addEventListener('pageshow', () => {
    leaving = false;
  });
}
/** Grace period for a cancelled download to be attributed to a navigation. */
const SETTLE_MS = 400;

/**
 * `React.lazy` that survives a new deployment. A tab opened before a deploy
 * asks for page code by its old hashed name, which no longer exists; the host
 * answers with index.html and the import fails. Reload once to pick up the new
 * version instead of crashing — unless the page is already being left.
 */
export function lazyPage<T extends ComponentType<object>>(load: () => Promise<{ default: T }>) {
  return lazy(() =>
    load().catch(async (error: unknown) => {
      await new Promise((resolve) => setTimeout(resolve, SETTLE_MS));
      if (leaving) return new Promise<{ default: T }>(() => {});
      const message = String((error as Error)?.message ?? error).slice(0, 200);
      // Usually a deploy replaced this page's code: reloading once recovers, so that's only a warning.
      if (tryStaleChunkReload(() => reportError('stale_page_reloaded', { message }, undefined, 'warning'))) {
        return new Promise<{ default: T }>(() => {});
      }
      reportError('page_code_load_failed', { message }, error);
      throw error;
    }),
  );
}

/** Last line of defence: a readable screen with a way out instead of a blank page. */
export class RootErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError('render_crash', { message: error.message.slice(0, 200), stack: info.componentStack?.slice(0, 300) ?? null }, error);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <FullScreenMessage
        title="Something went wrong"
        action={
          <div className="flex flex-wrap justify-center gap-3">
            <Button onClick={() => window.location.reload()}>Reload</Button>
            <Button variant="secondary" onClick={() => window.location.assign('/')}>
              Go home
            </Button>
          </div>
        }
      >
        This page hit a problem. Reloading usually fixes it.
      </FullScreenMessage>
    );
  }
}
